/**
 * Journey window defaulting + date-containment helpers (journeys overhaul P2,
 * rules J2/J4, decision D2).
 *
 * A race journey is `[startYmd → raceYmd]`. When the user hasn't picked a
 * window explicitly, the default is the typical build length for the event's
 * distance — sourced from the training-plan library lengths where available,
 * falling back to static presets — capped by the runway to race day and
 * floored at 1 week (a race this week is a 1-week journey: exactly the
 * race-day box, case H).
 *
 * Pure module (no React, no fetch) so the funnel hook, the wizard, and the
 * onboarding form all share one set of rules — and it unit-tests directly.
 */
import { addDaysYmd, compareYmd } from "./dates";
import type { JourneyView } from "./journeyApi";

/** Static preset build lengths (weeks) for distances with no library plans (D2). */
export const FALLBACK_WINDOW_WEEKS: readonly number[] = [8, 12, 16];

export type JourneyWindowDefault = {
  /** Window start (YYYY-MM-DD), never before today. */
  startYmd: string;
  /** Window span in whole weeks (>= 1). */
  weeks: number;
  /**
   * True when the runway to race day is shorter than every typical build
   * length — the window starts today ("build shorter than typical: start now").
   */
  cappedByRunway: boolean;
};

/** Days from `fromYmd` to `toYmd` (positive when `toYmd` is later). */
export function daysBetweenYmd(fromYmd: string, toYmd: string): number {
  return Math.round((Date.parse(toYmd) - Date.parse(fromYmd)) / 86_400_000);
}

/** The start date for an N-week window ending on race day (inclusive span). */
export function windowStartYmd(raceYmd: string, weeks: number): string {
  return addDaysYmd(raceYmd, -(weeks * 7 - 1));
}

/** Whether an N-week window ending on race day starts today or later. */
function fitsRunway(raceYmd: string, todayYmd: string, weeks: number): boolean {
  return daysBetweenYmd(todayYmd, raceYmd) >= weeks * 7 - 1;
}

/** Whole weeks in the `[today → race]` runway, floored at 1 ("start today"). */
export function runwayWeeks(raceYmd: string, todayYmd: string): number {
  const days = daysBetweenYmd(todayYmd, raceYmd);
  return Math.max(1, Math.ceil((days + 1) / 7));
}

/**
 * Candidate window lengths (weeks) to offer for a race: the library plan
 * lengths for the distance when available, else the static presets — each
 * capped by the runway — plus the "start today" runway length itself.
 * Ascending, deduped, every entry >= 1.
 */
export function journeyWindowChoices(
  libraryLengths: number[] | null | undefined,
  raceYmd: string,
  todayYmd: string
): number[] {
  const presets =
    libraryLengths && libraryLengths.length > 0 ? libraryLengths : [...FALLBACK_WINDOW_WEEKS];
  const runway = runwayWeeks(raceYmd, todayYmd);
  const out = new Set<number>();
  for (const w of presets) {
    if (w >= 1 && fitsRunway(raceYmd, todayYmd, w)) out.add(w);
  }
  out.add(runway); // "start today" is always on offer
  return [...out].sort((a, b) => a - b);
}

/**
 * The defaulted journey window per D2: the LONGEST typical build that still
 * fits the runway (so the full base phase lands); when none fit, start today.
 * Race today/past degrades to a 1-week window ending on race day.
 */
export function defaultJourneyWindow(opts: {
  raceYmd: string;
  todayYmd: string;
  /** Library plan lengths for the distance; null/empty → static presets. */
  libraryLengths?: number[] | null;
}): JourneyWindowDefault {
  const { raceYmd, todayYmd, libraryLengths } = opts;
  if (compareYmd(raceYmd, todayYmd) <= 0) {
    // Race today or already past — a 1-week window ending on race day. Callers
    // gate past races out of the funnel; this keeps the helper total.
    return { startYmd: windowStartYmd(raceYmd, 1), weeks: 1, cappedByRunway: true };
  }
  const presets =
    libraryLengths && libraryLengths.length > 0
      ? [...libraryLengths].sort((a, b) => a - b)
      : [...FALLBACK_WINDOW_WEEKS];
  const fitting = presets.filter((w) => w >= 1 && fitsRunway(raceYmd, todayYmd, w));
  if (fitting.length > 0) {
    const weeks = fitting[fitting.length - 1];
    return { startYmd: windowStartYmd(raceYmd, weeks), weeks, cappedByRunway: false };
  }
  // Runway shorter than every typical build → start today.
  const weeks = runwayWeeks(raceYmd, todayYmd);
  return { startYmd: todayYmd, weeks, cappedByRunway: true };
}

/**
 * The [start, end] YMD span a journey's window covers, or null when the
 * journey carries no usable window. Race journeys run planStart → race day;
 * rolling journeys (no raceYmd) run anchor → their last derived box window.
 * Defensive fallbacks keep a span on journeys that predate the stored-window
 * migration. (Relocated from the retired `journeyBands.ts` — the band lanes
 * are gone, the span outlived them.)
 */
export function journeyWindowSpan(
  j: Pick<JourneyView, "drops"> &
    Partial<Pick<JourneyView, "raceYmd" | "planStartYmd" | "anchorYmd">>
): { startYmd: string; endYmd: string } | null {
  const firstDrop = j.drops[0];
  const lastDrop = j.drops[j.drops.length - 1];
  const startYmd = j.planStartYmd ?? j.anchorYmd ?? firstDrop?.startYmd ?? null;
  const endYmd = j.raceYmd ?? lastDrop?.endYmd ?? null;
  if (!startYmd || !endYmd || endYmd < startYmd) return null;
  return { startYmd, endYmd };
}

/**
 * The active race journey whose window contains `ymd`, or null. Containment is
 * `start <= ymd < raceYmd` — the race day itself is the same-event replace
 * case (the wizard's existing confirm), not a tune-up question. The window
 * start prefers the stored/plan-derived `planStartYmd`, then the first drop's
 * window, then race day itself (a race-day-only journey covers just that day —
 * excluded here by the strict `< raceYmd`).
 */
export function journeyCoveringDate(
  journeys: readonly JourneyView[],
  ymd: string
): JourneyView | null {
  for (const j of journeys) {
    if (!j.raceYmd) continue;
    if (j.journeyType === "rolling") continue; // rolling windows derive elsewhere (P3)
    const start = j.planStartYmd ?? j.drops[0]?.startYmd ?? j.raceYmd;
    if (compareYmd(start, ymd) <= 0 && compareYmd(ymd, j.raceYmd) < 0) return j;
  }
  return null;
}

/**
 * J4 gate: the goal-vs-milestone question is asked ONLY when the picked date
 * falls inside an active journey's window. Open-time picks skip it entirely
 * (the inline window row / confirm sheet is the only stop).
 */
export function needsIntentQuestion(journeys: readonly JourneyView[], ymd: string): boolean {
  return journeyCoveringDate(journeys, ymd) != null;
}

/**
 * 5b — plain-language impact of creating a race plan across `[startYmd →
 * raceYmd]`, computed from REAL journey data (never hardcoded counts) for the
 * `RacePlanWindowConfirm` caption block. Because a build-method race plan is
 * ADDITIVE (training-plans/service.ts creates fresh workouts, deletes nothing),
 * the summary reassures rather than warns: existing sessions stay put, and a
 * rolling journey merely resumes after the race.
 */
export type RacePlanImpact = {
  /** Existing planned workouts inside the new window, summed from the caller's
   *  journeys' per-drop `workoutCount`. The "your N stay exactly where they are"
   *  line is omitted when 0. */
  existingWorkoutsInWindow: number;
  /** Race day an interrupted rolling journey resumes after (the new race's day),
   *  or null when no active rolling journey overlaps the window. Drives "your
   *  flexible training picks up again after {date}". */
  rollingResumesAfterYmd: string | null;
  /** True when a non-rolling journey's window overlaps the new window, so its
   *  fuel boxes re-group around the added race. Drives the box-regroup line. */
  boxesRegroup: boolean;
};

/**
 * Count the existing planned workouts an active journey contributes to the
 * `[startYmd, raceYmd]` span, from its per-drop `workoutCount` — the same set
 * the box cards already show, so the summary and the calendar agree. A drop
 * counts when its window intersects the span (inclusive).
 */
export function computeRacePlanImpact(
  journeys: readonly JourneyView[],
  window: { startYmd: string; raceYmd: string }
): RacePlanImpact {
  const { startYmd, raceYmd } = window;
  let existing = 0;
  let rollingResumesAfterYmd: string | null = null;
  let boxesRegroup = false;
  for (const j of journeys) {
    // Only currently-live journeys reshape around the new race.
    if (j.status && j.status !== "active") continue;
    let overlaps = false;
    for (const d of j.drops) {
      if (compareYmd(d.startYmd, raceYmd) <= 0 && compareYmd(startYmd, d.endYmd) <= 0) {
        overlaps = true;
        existing += d.workoutCount ?? 0;
      }
    }
    if (!overlaps) continue;
    if (j.journeyType === "rolling") {
      // The rolling journey front-truncates to resume the day after the race.
      rollingResumesAfterYmd = raceYmd;
    } else {
      // An existing race journey's boxes re-slice around the added race.
      boxesRegroup = true;
    }
  }
  return { existingWorkoutsInWindow: existing, rollingResumesAfterYmd, boxesRegroup };
}

/** One segment of a generic (no-library-plan) phase bar. */
export type GenericPhaseSegment = { label: string; color: string; weeks: number };

// Same periodization palette as the wizard's library phase bars.
const GENERIC_PHASES = [
  { label: "Base", color: "#E4EFA1", share: 0.35 },
  { label: "Build", color: "#C5DC8A", share: 0.3 },
  { label: "Peak", color: "#88B655", share: 0.2 },
  { label: "Taper", color: "#D8C7F2", share: 0.15 }
] as const;

/**
 * Generic build/peak/taper shading for a custom window's phase bar (D2 — the
 * "no library plan for this distance" variant). Deterministic split that always
 * sums to `weeks`; 1-week windows are all Race.
 */
export function genericPhaseSegments(weeks: number): GenericPhaseSegment[] {
  const w = Math.max(1, Math.round(weeks));
  if (w === 1) return [{ label: "Race", color: "#FFD176", weeks: 1 }];
  const out: GenericPhaseSegment[] = [];
  let used = 0;
  for (let i = GENERIC_PHASES.length - 1; i >= 1; i -= 1) {
    // Allocate Taper → Peak → Build (min 1wk each while room remains); Base
    // absorbs the remainder so long windows read as mostly base.
    const p = GENERIC_PHASES[i];
    const alloc = Math.min(Math.max(1, Math.round(w * p.share)), w - used - 1);
    if (alloc <= 0) continue;
    out.unshift({ label: p.label, color: p.color, weeks: alloc });
    used += alloc;
  }
  out.unshift({ label: "Base", color: GENERIC_PHASES[0].color, weeks: w - used });
  return out.filter((s) => s.weeks > 0);
}
